import { RiskWorkspace } from '@app/types/workspaceTypes'
import { Button } from '@src/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@src/components/ui/tooltip'
import { cn } from '@src/lib/tailwind.utils'
import { Check, ChevronsUpDown, Folder, InfoIcon } from 'lucide-react'
import { getWorkspaceMeta, isWorkspaceSelectable } from '../utils/workspaceSelector.utils'

export type WorkspaceSelectorViewProps = {
  workspaces: RiskWorkspace[]
  selectedWorkspace: RiskWorkspace | null
  isLoading?: boolean
  isError?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (workspace: RiskWorkspace) => void
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  className?: string
}

const WorkspaceSelectorView = ({
  label = '',
  placeholder = 'Select a Workspace',
  searchPlaceholder = 'Search workspaces',
  workspaces,
  selectedWorkspace,
  isLoading = false,
  isError = false,
  open,
  onOpenChange,
  onSelect,
  className,
}: WorkspaceSelectorViewProps) => {
  const triggerText = isLoading
    ? 'Loading workspaces...'
    : isError
      ? 'Unable to load workspaces'
      : (selectedWorkspace?.name ?? placeholder)

  return (
    <div className={cn('', className)}>
      <span className="px-1 text-xs font-medium text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading || isError}
            className={cn(
              'h-14 w-96 justify-between rounded-xl border-2 bg-background py-1 shadow-sm',
              'data-[state=open]:border-rexBlue-500 data-[state=open]:ring-2 data-[state=open]:ring-rexBlue-500/30',
              open && 'border-rexBlue-500 ring-2 ring-rexBlue-500/30',
            )}
          >
            <span className="flex min-w-0 items-center gap-4 text-left">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Folder className="h-4 w-4" />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-bold uppercase leading-none text-foreground">
                  Workspace
                </span>
                <span className="mt-1 truncate text-xs font-normal leading-none text-foreground">
                  {triggerText}
                </span>
              </span>
            </span>
            <ChevronsUpDown className="h-5 w-5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 rounded-md p-2 shadow-lg" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="h-10 text-sm" />
            <CommandList>
              <CommandEmpty>No workspaces found.</CommandEmpty>
              <CommandGroup className="py-2">
                <TooltipProvider delayDuration={0}>
                  {workspaces.map((workspace) => {
                    const disabled = !isWorkspaceSelectable(workspace)
                    const selected = selectedWorkspace?.id === workspace.id

                    return (
                      <CommandItem
                        key={workspace.id}
                        value={workspace.name}
                        disabled={disabled}
                        onSelect={() => {
                          if (disabled) return
                          onSelect(workspace)
                        }}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-transparent data-[disabled=true]:opacity-60',
                          selected && !disabled && 'bg-muted',
                        )}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Folder className="h-4 w-4" />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-normal text-foreground">
                            {workspace.name}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {getWorkspaceMeta(workspace)}
                          </span>
                        </span>
                        {disabled ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm">
                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Workspaces need both hazards and elements.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0 text-muted-foreground',
                              selected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        )}
                      </CommandItem>
                    )
                  })}
                </TooltipProvider>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default WorkspaceSelectorView
